import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { ref as dbRef, push, set, onValue, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL, getStorage, deleteObject } from 'firebase/storage';
import { database } from '../firebase';

// Material-UI Components - now properly imported individually
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';

// Material-UI Icons
import AddPhotoAlternate from '@mui/icons-material/AddPhotoAlternate';
import Movie from '@mui/icons-material/Movie';
import Close from '@mui/icons-material/Close';
import Send from '@mui/icons-material/Send';
import Category from '@mui/icons-material/Category';
import TitleIcon from '@mui/icons-material/Title';
import DescriptionIcon from '@mui/icons-material/Description';
import DeleteIcon from '@mui/icons-material/Delete';

const MediaUploader = () => {
  // Initialize storage
  const storage = getStorage();
  
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    mediaType: 'photo'
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png'],
      'video/*': ['.mp4', '.webm']
    },
    maxFiles: 5,
    onDrop: acceptedFiles => {
      setFiles(prev => [
        ...prev,
        ...acceptedFiles.map(file => ({
          file,
          id: uuidv4(),
          preview: URL.createObjectURL(file),
          type: file.type.startsWith('image') ? 'image' : 'video'
        }))
      ]);
    }
  });

  useEffect(() => {
    const galleryRef = dbRef(database, 'school/gallery');
    const unsubscribe = onValue(galleryRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const items = Object.entries(data).map(([id, item]) => ({
          id,
          ...item
        }));
        // Sort by newest first
        items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setUploadedMedia(items);
      } else {
        setUploadedMedia([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.title || 'Untitled'}"?`)) return;

    try {
      // 1. Attempt to delete files from Firebase Storage
      if (item.files && item.files.length > 0) {
        for (const file of item.files) {
          try {
            // Extract the path from the download URL
            const urlObj = new URL(file.url);
            const pathMatch = urlObj.pathname.match(/\/o\/(.+)$/);
            if (pathMatch) {
              const decodedPath = decodeURIComponent(pathMatch[1]);
              const fileStorageRef = storageRef(storage, decodedPath);
              await deleteObject(fileStorageRef);
            }
          } catch (e) {
            console.warn("Failed to delete file from storage, proceeding to delete DB entry", e);
          }
        }
      }

      // 2. Delete entry from Realtime Database
      await remove(dbRef(database, `school/gallery/${item.id}`));
      
      // We can reuse success state to show deletion
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Failed to delete item.');
    }
  };

  const removeFile = id => {
    setFiles(files.filter(file => file.id !== id));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const uploadToStorage = async (file) => {
    const fileRef = storageRef(storage, `school-media/${file.id}_${file.file.name}`);
    await uploadBytes(fileRef, file.file);
    return getDownloadURL(fileRef);
  };

const handleSubmit = async () => {
  if (files.length === 0) return;
  
  setUploading(true);
  setProgress(0);
  setError(null);
  setSuccess(false);

  try {
    // Upload all files to storage
    const uploadPromises = files.map(file => uploadToStorage(file));
    const urls = await Promise.all(uploadPromises);

    // Prepare data for database
    const mediaData = {
      ...formData,
      files: files.map((file, index) => ({
        url: urls[index],
        type: file.type,
        name: file.file.name
      })),
      createdAt: new Date().toISOString()
    };

    // Save to database under school/gallery node
    const galleryRef = push(dbRef(database, 'school/gallery'));
    await set(galleryRef, mediaData);

    setProgress(100);
    setSuccess(true);
    setTimeout(() => {
      setFiles([]);
      setFormData({
        title: '',
        description: '',
        category: 'general',
        mediaType: 'photo'
      });
      setUploading(false);
      setProgress(0);
    }, 1500);
  } catch (err) {
    console.error('Upload failed:', err);
    setError('Failed to upload media. Please try again.');
    setUploading(false);
  }
};

  return (
    <Card sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
      <CardHeader
        title="School Media Upload"
        subheader="Add photos and videos to the school website"
      />
      <Divider />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Media uploaded successfully!
          </Alert>
        )}

        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <TitleIcon color="action" sx={{ mr: 1 }} />
              ),
            }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            InputProps={{
              startAdornment: (
                <DescriptionIcon color="action" sx={{ mr: 1 }} />
              ),
            }}
          />

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={formData.category}
                onChange={handleChange}
                label="Category"
                startAdornment={<Category color="action" />}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="events">Events</MenuItem>
                <MenuItem value="classes">Classes</MenuItem>
                <MenuItem value="sports">Sports</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Media Type</InputLabel>
              <Select
                name="mediaType"
                value={formData.mediaType}
                onChange={handleChange}
                label="Media Type"
              >
                <MenuItem value="photo">Photos</MenuItem>
                <MenuItem value="video">Videos</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Card
              variant="outlined"
              sx={{
                p: 4,
                borderStyle: 'dashed',
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? 'action.hover' : 'background.paper'
              }}
            >
              {formData.mediaType === 'photo' ? (
                <AddPhotoAlternate fontSize="large" color="action" />
              ) : (
                <Movie fontSize="large" color="action" />
              )}
              <Typography variant="body1" sx={{ mt: 1 }}>
                {isDragActive
                  ? 'Drop files here'
                  : `Drag & drop ${formData.mediaType === 'photo' ? 'photos' : 'videos'} here, or click to browse`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.mediaType === 'photo'
                  ? 'Supports: JPG, PNG'
                  : 'Supports: MP4, WebM'}
              </Typography>
            </Card>
          </div>

          {files.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Selected Files:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {files.map(file => (
                  <Chip
                    key={file.id}
                    label={file.file.name}
                    icon={file.type === 'image' ? <AddPhotoAlternate /> : <Movie />}
                    onDelete={() => removeFile(file.id)}
                    deleteIcon={<Close />}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            {uploading && (
              <>
                <CircularProgress variant="determinate" value={progress} size={24} />
                <Typography variant="body2">{progress}%</Typography>
              </>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={files.length === 0 || uploading}
              startIcon={<Send />}
            >
              Upload Media
            </Button>
          </Stack>
        </Stack>
      </CardContent>

      <Divider />
      <CardHeader 
        title="Uploaded Media Gallery" 
        subheader="Manage and delete existing gallery items" 
      />
      <Divider />
      <CardContent>
        {uploadedMedia.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
            No media uploaded yet.
          </Typography>
        ) : (
          <List sx={{ width: '100%', bgcolor: 'background.paper', maxHeight: 400, overflow: 'auto' }}>
            {uploadedMedia.map((item) => {
              const firstFile = item.files && item.files.length > 0 ? item.files[0] : null;
              // strip token to prevent unnecessary downloads
              const previewUrl = firstFile ? firstFile.url.replace(/&token=[^&]+/, '').replace(/\?token=[^&]+&?/, '?') : undefined;
              const isVideo = firstFile ? (firstFile.type === 'video' || (previewUrl && previewUrl.match(/\.(mp4|webm|mov)$/i))) : (item.mediaType === 'video');
              
              return (
                <ListItem
                  key={item.id}
                  secondaryAction={
                    <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(item)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  }
                  sx={{ borderBottom: '1px solid #f0f0f0' }}
                >
                  <ListItemAvatar>
                    <Avatar 
                      variant="rounded" 
                      src={!isVideo && previewUrl ? previewUrl : undefined}
                      sx={{ width: 50, height: 50, mr: 2 }}
                    >
                      {isVideo ? <Movie /> : <AddPhotoAlternate />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.title || 'Untitled'}
                    secondary={`${item.category} • ${item.files ? item.files.length : 0} file(s) • ${new Date(item.createdAt).toLocaleDateString()}`}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default MediaUploader;